import { finalize, Observable, take } from 'rxjs';

import { Container, Service } from '../../../symphony';

import { SlDiscussContext } from '../global-constants';
import { SlDataService } from './sl-data.service';
import {
	IDiscussApi, DiscussApi, IDiscussPostData, IDiscussVoteResponseData, IPostQuestionPayload, IDiscussAddReplyResponseData, IGetTagsResponse, IDiscussRepliesResponseData, IDiscussSearchParams, DiscussSearchResponseDataType, IDiscussDetailsResponseDataType, IDiscussToggleAcceptedAnswerResponseData,
} from '../../../api/public/discuss-api';

@Service()
export class SlApiService {
	private dataService = Container.take(SlDiscussContext, SlDataService);

	private discussApi: IDiscussApi;

	constructor() {
		this.discussApi = Container.take('global', DiscussApi);
	}

	public getDiscussQuestion(id: string): Observable<IDiscussPostData> {
		return this.discussApi.getQuestion(+id).pipe(take(1));
	}

	public votePost(
		id: string,
		vote: number,
	): Observable<IDiscussVoteResponseData> {
		return this.discussApi.votePost(+id, vote).pipe(take(1));
	}

	public postQuestion(
		payload: IPostQuestionPayload,
	): Observable<IDiscussAddReplyResponseData> {
		return this.discussApi.postQuestion(payload).pipe(take(1));
	}

	public editQuestion(
		payload: IPostQuestionPayload,
		id: number,
	): Observable<IDiscussPostData> {
		return this.discussApi
			.editPost(id, payload.message, payload.tags, payload.title)
			.pipe(take(1));
	}

	public deleteQuestion(id: number): Observable<object> {
		return this.discussApi.deletePost(id).pipe(take(1));
	}

	public deleteReply(id: number): Observable<object> {
		return this.discussApi.deletePost(id).pipe(take(1));
	}

	public getTags(tagInput: string): Observable<IGetTagsResponse> {
		return this.discussApi.getTags(tagInput).pipe(take(1));
	}

	public getReplies(
		questionId: number,
		index: number,
		count: number,
		orderBy: number,
	): Observable<IDiscussRepliesResponseData> {
		return this.discussApi
			.getReplies(questionId, index, count, orderBy)
			.pipe(take(1));
	}

	public createReply(
		questionId: number,
		replyContent: string,
	): Observable<IDiscussAddReplyResponseData> {
		this.dataService.isSaveReplyLoading$.next(true);
		return this.discussApi.createReply(questionId, replyContent).pipe(
			take(1),
			finalize(() => {
				this.dataService.isSaveReplyLoading$.next(false);
			}),
		);
	}

	public editReply(
		replyId: number,
		replyContent: string,
	): Observable<IDiscussPostData> {
		this.dataService.isSaveReplyLoading$.next(true);
		return this.discussApi.editPost(replyId, replyContent, null, null).pipe(
			take(1),
			finalize(() => {
				this.dataService.isSaveReplyLoading$.next(false);
			}),
		);
	}

	public discussSearch({
		count,
		filter,
		index,
		query,
		profileId,
	}: IDiscussSearchParams): Observable<DiscussSearchResponseDataType> {
		return this.discussApi
			.discussSearch({
				count,
				filter,
				index,
				query,
				profileId,
			})
			.pipe(take(1));
	}

	public getDiscussDetails({
		count,
		filter,
		index,
		query,
		profileId,
	}: IDiscussSearchParams): Observable<IDiscussDetailsResponseDataType> {
		return this.discussApi
			.getDiscussDetails({
				count,
				filter,
				index,
				query,
				profileId,
			})
			.pipe(take(1));
	}

	public toggleAcceptedAnswer(
		id: number,
		accepted: boolean,
	): Observable<IDiscussToggleAcceptedAnswerResponseData> {
		return this.discussApi.toggleAcceptedAnswer(id, accepted).pipe(take(1));
	}
}
